import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useAuthStore, useUIStore } from '../../store/authStore';
import {
  LayoutDashboard,
  FileText,
  Upload,
  Users,
  History,
  Building2,
  UserCheck,
  Send,
  Settings,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const fontSizeStyles = {
  small: { fontSize: '14px' },
  medium: { fontSize: '16px' },
  large: { fontSize: '18px' },
};

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
  { path: '/reports', icon: FileText, labelKey: 'nav.reports' },
  { path: '/import', icon: Upload, labelKey: 'nav.import' },
];

const settingsItems = [
  { path: '/admin/settings', icon: Settings, labelKey: 'nav.settings' },
];

const adminItems = [
  { path: '/admin/users', icon: Users, labelKey: 'nav.users' },
  { path: '/admin/contractors', icon: Building2, labelKey: 'nav.contractors' },
  { path: '/admin/inspectors', icon: UserCheck, labelKey: 'nav.inspectors' },
  { path: '/admin/audit-logs', icon: History, labelKey: 'nav.auditLogs' },
];

export default function Sidebar({ isOpen, onClose, onOpenLinePanel }) {
  const { t } = useTranslation();
  const { isAdmin, canSendLine, canManageSettings, isContractor } = useAuthStore();

  const visibleNavItems = navItems.filter((item) => !(item.path === '/import' && isContractor()));
  const visibleAdminItems = [
    ...(isAdmin() ? adminItems : []),
    ...(canManageSettings() ? settingsItems : []),
  ];
  const { fontSize } = useUIStore();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-gray-200 z-40 transition-transform duration-200 flex flex-col',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={fontSizeStyles[fontSize]}
      >
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 p-1 text-gray-500 hover:text-gray-700"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Nav links */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors',
                  isActive
                    ? 'bg-tpc-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {t(item.labelKey)}
            </NavLink>
          ))}

          {/* Admin section */}
          {visibleAdminItems.length > 0 && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-4 text-[0.75em] font-semibold text-gray-400 uppercase tracking-wider">
                  {t('nav.admin')}
                </p>
              </div>

              {visibleAdminItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors',
                      isActive
                        ? 'bg-tpc-blue text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    )
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* LINE Send button — pinned to the bottom */}
        {canSendLine() && (
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={() => { onOpenLinePanel(); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
            >
              <Send className="w-5 h-5 flex-shrink-0" />
              {t('dashboard.sendLineReport', 'LINE 通報發送')}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
